import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router';
import { motion, AnimatePresence, useMotionValue, animate } from 'motion/react';
import { Calendar, Clock, MapPin, Check, X, Heart, Users, Camera, Cake, Utensils, ChevronLeft, ChevronRight, Sparkles, Palette, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Navigation } from '../components/Navigation';
import { useAuth, PREFERENCES_ENABLED, EVENT_PASSED } from '../context/AuthContext';
import { rsvpAPI } from '../api/apiAdapter';
import { ImageWithFallback } from '../components/figma/ImageWithFallback';
import { PHOTO_PATHS } from '../constants/appPhotos';
import { useMediaUrls, useDressCodeUrls } from '../hooks/useGalleryMedia';

const MIN_DRESS_SLOTS = 6;
const PHOTOGRAPHER_GALLERY_URL =
  'https://61a8a4631b7013-24354813.gallery.photo/gallery/22-05-26-alina-i-vana-h6rti5/';

const timeline = [
  { time: '15:30', icon: Users, title: 'Сбор гостей', description: 'Приветствие и регистрация гостей' },
  { time: '16:00', icon: Heart, title: 'Церемония', description: 'Выездная регистрация брака' },
  { time: '16:30', icon: Camera, title: 'Фотосессия', description: 'Групповые и индивидуальные фотографии' },
  { time: '17:00', icon: Utensils, title: 'Банкет', description: 'Праздничный ужин с развлечениями' },
  { time: '22:00', icon: Cake, title: 'Время торта', description: 'Сладкое угощение для всех гостей' },
  { time: '23:00', icon: Sparkles, title: 'Сказка заканчивается', description: 'Праздник завершается, но тепло этого вечера остается в каждом из нас' },
];

const colorPalette = [
  // Голубые оттенки
  { name: 'Стальной голубой', hex: '#7a8c99', rgb: 'rgb(122, 140, 153)', category: 'Голубые', description: 'Благородный серо-голубой, напоминающий небо перед рассветом', usage: 'Акценты, текстиль' },
  { name: 'Серебристо-голубой', hex: '#99b4c4', rgb: 'rgb(153, 180, 196)', category: 'Голубые', description: 'Мягкий голубой с лёгким серебристым отливом', usage: 'Декор, детали' },
  { name: 'Светло-голубой', hex: '#c4dae6', rgb: 'rgb(196, 218, 230)', category: 'Голубые', description: 'Нежный небесно-голубой, воздушный и прозрачный', usage: 'Фон, ткани' },
  { name: 'Ледяной', hex: '#d9ecf5', rgb: 'rgb(217, 236, 245)', category: 'Голубые', description: 'Почти белый с едва уловимым голубым подтоном', usage: 'Легкие акценты' },
  // Лавандовые оттенки
  { name: 'Серо-лавандовый', hex: '#9d98ae', rgb: 'rgb(157, 152, 174)', category: 'Лавандовые', description: 'Приглушённый лавандовый с дымчатым серым подтоном', usage: 'Основной цвет' },
  { name: 'Светло-лавандовый', hex: '#b8b4c8', rgb: 'rgb(184, 180, 200)', category: 'Лавандовые', description: 'Пастельный лавандовый, мягкий и романтичный', usage: 'Флористика' },
  { name: 'Лиловый', hex: '#c9c4db', rgb: 'rgb(201, 196, 219)', category: 'Лавандовые', description: 'Светлый лиловый с выраженным фиолетовым тоном', usage: 'Декор' },
  { name: 'Нежно-лиловый', hex: '#d9d4eb', rgb: 'rgb(217, 212, 235)', category: 'Лавандовые', description: 'Очень светлый лавандовый, почти прозрачный', usage: 'Детали' },
  // Фиолетово-серые оттенки
  { name: 'Темный лавандовый', hex: '#6d6578', rgb: 'rgb(109, 101, 120)', category: 'Фиолетовые', description: 'Тёмный серо-лавандовый с глубоким фиолетовым характером', usage: 'Глубокие акценты' },
  { name: 'Лавандово-серый', hex: '#8d7f94', rgb: 'rgb(141, 127, 148)', category: 'Фиолетовые', description: 'Средний серо-фиолетовый, насыщенный и элегантный', usage: 'Текстиль' },
  // Розово-лавандовые
  { name: 'Розово-лавандовый', hex: '#c4a8bc', rgb: 'rgb(196, 168, 188)', category: 'Розовые', description: 'Тёплый розовый с явным лавандовым переливом', usage: 'Цветы' },
  { name: 'Светлый розово-лавандовый', hex: '#d4bfce', rgb: 'rgb(212, 191, 206)', category: 'Розовые', description: 'Нежный розово-лавандовый, воздушный и едва уловимый', usage: 'Акценты' },
  // Нежно-розовые
  { name: 'Пыльная роза', hex: '#d9b8cf', rgb: 'rgb(217, 184, 207)', category: 'Розовые', description: 'Пыльно-розовый с заметным лиловым отливом', usage: 'Букеты' },
  { name: 'Персиково-розовый', hex: '#b8908d', rgb: 'rgb(184, 144, 141)', category: 'Розовые', description: 'Приглушённый тёплый розовый с землистым оттенком', usage: 'Детали' },
  { name: 'Светло-розовый', hex: '#d9b4b8', rgb: 'rgb(217, 180, 184)', category: 'Розовые', description: 'Нежный пастельный розовый с лёгкой теплотой', usage: 'Декор' },
  // Персиковые и коралловые
  { name: 'Коралловый', hex: '#e6a8a0', rgb: 'rgb(230, 168, 160)', category: 'Персиковые', description: 'Мягкий пастельный коралловый с тёплым розовым характером', usage: 'Яркие акценты' },
  { name: 'Персиковый', hex: '#e6c4a8', rgb: 'rgb(230, 196, 168)', category: 'Персиковые', description: 'Классический мягкий персиковый с тёплым золотистым тоном', usage: 'Теплые тона' },
  { name: 'Нежно-персиковый', hex: '#f5d4c4', rgb: 'rgb(245, 212, 196)', category: 'Персиковые', description: 'Очень светлый персиковый, нежный как лепесток', usage: 'Мягкие акценты' },
  // Бежевые и кремовые
  { name: 'Бежево-розовый', hex: '#e6d4c9', rgb: 'rgb(230, 212, 201)', category: 'Бежевые', description: 'Тёплый бежевый с нежным розоватым подтоном', usage: 'Фон' },
  { name: 'Песочный', hex: '#d9c4a8', rgb: 'rgb(217, 196, 168)', category: 'Бежевые', description: 'Натуральный тёплый бежевый, как морской песок', usage: 'Натуральные акценты' },
  { name: 'Светлый беж', hex: '#e6d4b8', rgb: 'rgb(230, 212, 184)', category: 'Бежевые', description: 'Классический светлый беж с тёплым золотистым тоном', usage: 'База' },
  { name: 'Сливочный', hex: '#f5e6c9', rgb: 'rgb(245, 230, 201)', category: 'Бежевые', description: 'Мягкий сливочно-кремовый, почти молочный', usage: 'Светлые тона' },
  { name: 'Слоновая кость', hex: '#f5f5e6', rgb: 'rgb(245, 245, 230)', category: 'Бежевые', description: 'Благородная слоновая кость с тёплым желтоватым отливом', usage: 'Основа' },
  // Серо-коричневые
  { name: 'Тауп', hex: '#a89490', rgb: 'rgb(168, 148, 144)', category: 'Серо-коричневые', description: 'Классический тауп — тёплый серо-коричневый с розоватым нюансом', usage: 'Нейтральный' },
  { name: 'Светлый тауп', hex: '#b8a8a0', rgb: 'rgb(184, 168, 160)', category: 'Серо-коричневые', description: 'Светлый серо-бежевый с мягким розовато-коричневым тоном', usage: 'Дополнение' },
  { name: 'Бежево-серый', hex: '#c9b8b4', rgb: 'rgb(201, 184, 180)', category: 'Серо-коричневые', description: 'Тёплый серо-бежевый с лёгким розоватым подтоном', usage: 'Переходы' },
  { name: 'Пепельно-розовый', hex: '#e6d9d4', rgb: 'rgb(230, 217, 212)', category: 'Серо-коричневые', description: 'Очень светлый пепельно-розовый, почти белый', usage: 'Мягкий фон' },
  // Серые
  { name: 'Серо-лиловый', hex: '#8d8494', rgb: 'rgb(141, 132, 148)', category: 'Серые', description: 'Холодный серый с отчётливым лиловым подтоном', usage: 'Контраст' },
  { name: 'Светло-серый', hex: '#a89ca8', rgb: 'rgb(168, 156, 168)', category: 'Серые', description: 'Светло-серый с тонким лавандово-розоватым подтоном', usage: 'Нейтраль' },
  { name: 'Пастельный серый', hex: '#c9c4d4', rgb: 'rgb(201, 196, 212)', category: 'Серые', description: 'Воздушный серый с нежным фиолетовым отливом', usage: 'Легкий контраст' },
];

const paletteCategories = ['Все', 'Голубые', 'Лавандовые', 'Фиолетовые', 'Розовые', 'Персиковые', 'Бежевые', 'Серо-коричневые', 'Серые'];

// Координаты Sky-village (53°20′22″N, 50°11′53″E)
const LOCATION_COORDS = {
  lat: 53.339416,
  lng: 50.198036
};

/** Официальная дата бракосочетания */
const MARRIAGE_START_DATE = new Date(2026, 3, 18);

function getDaysMarried(): number {
  const today = new Date();
  const start = new Date(MARRIAGE_START_DATE);
  today.setHours(0, 0, 0, 0);
  start.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((today.getTime() - start.getTime()) / 86_400_000));
}

function formatDaysWord(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return 'дней';
  if (mod10 === 1) return 'день';
  if (mod10 >= 2 && mod10 <= 4) return 'дня';
  return 'дней';
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    const m = window.matchMedia(query);
    setMatches(m.matches);
    const handler = () => setMatches(m.matches);
    m.addEventListener('change', handler);
    return () => m.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

export const Event: React.FC = () => {
  const { user } = useAuth();
  const [rsvp, setRsvp] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedColor, setSelectedColor] = useState<typeof colorPalette[0] | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('Все');
  const isDesktop = useMediaQuery('(min-width: 768px)');

  useEffect(() => {
    if (!selectedColor) return;

    const body = document.body;
    const html = document.documentElement;

    const originalBodyOverflow = body.style.overflow;
    const originalHtmlOverflow = html.style.overflow;
    const originalPaddingRight = body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    // Блокируем прокрутку фона, пока открыта карточка цвета.
    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;

    return () => {
      body.style.overflow = originalBodyOverflow;
      html.style.overflow = originalHtmlOverflow;
      body.style.paddingRight = originalPaddingRight;
    };
  }, [selectedColor]);

  const filteredColors = selectedCategory === 'Все'
    ? colorPalette
    : colorPalette.filter(color => color.category === selectedCategory);

  const { urls: heroUrls } = useMediaUrls([PHOTO_PATHS.heroGroom, PHOTO_PATHS.heroBride]);
  const { urls: dressCodeUrls } = useDressCodeUrls();
  // Количество элементов = число файлов из хранилища; только при пустом списке — заглушки для отображения
  const dressCodeImages = dressCodeUrls.length > 0 ? dressCodeUrls : Array(MIN_DRESS_SLOTS).fill('');
  const DRESS_COUNT = dressCodeImages.length;
  /** На мобилке (в т.ч. в landscape) открываем приложение карт устройства; при ширине > 896px — Яндекс в новой вкладке */
  const useMobileMap = useMediaQuery('(max-width: 896px)');

  // Ссылки для открытия точки в приложениях карт (координаты: lat, lng)
  const geoUri = `geo:${LOCATION_COORDS.lat},${LOCATION_COORDS.lng}`;
  const appleMapsUrl = `https://maps.apple.com/?ll=${LOCATION_COORDS.lat},${LOCATION_COORDS.lng}&q=Sky-village`;
  const yandexMapsUrl = `https://yandex.ru/maps/?ll=${LOCATION_COORDS.lng},${LOCATION_COORDS.lat}&z=14&pt=${LOCATION_COORDS.lng},${LOCATION_COORDS.lat}`;
  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${LOCATION_COORDS.lat},${LOCATION_COORDS.lng}`;
  // На iOS geo: не поддерживается — открываем Apple Maps. На Android geo: даёт выбор приложения.
  const handleMapClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (typeof navigator === 'undefined') return;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (isIOS) {
      e.preventDefault();
      window.location.href = appleMapsUrl;
    }
  };

  const dressSliderRef = useRef<HTMLDivElement>(null);
  const [dressMeasured, setDressMeasured] = useState({ step: 280, setWidth: DRESS_COUNT * 280 });

  useEffect(() => {
    const track = dressSliderRef.current;
    if (!track) return;
    const firstCard = track.firstElementChild as HTMLElement | null;
    if (!firstCard) return;
    const measure = () => {
      const gap = parseFloat(getComputedStyle(track).gap) || 24;
      const cardWidth = firstCard.offsetWidth;
      const step = cardWidth + gap;
      setDressMeasured({ step, setWidth: DRESS_COUNT * step });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(track);
    return () => ro.disconnect();
  }, [isDesktop, DRESS_COUNT]);

  const dressSetWidth = dressMeasured.setWidth;
  const dressCardStep = dressMeasured.step;

  const dressX = useMotionValue(0);
  const dressControlsRef = useRef<ReturnType<typeof animate> | null>(null);
  const dressManualControlsRef = useRef<ReturnType<typeof animate> | null>(null);
  const [dressPaused, setDressPaused] = useState(false);
  const [dressPausedX, setDressPausedX] = useState(0);
  const dressPausedXRef = useRef(0);
  const daysMarried = getDaysMarried();
  const dressTouchStartRef = useRef<{ x: number; clientX: number } | null>(null);
  const dressInitializedRef = useRef(false);
  /** После нажатия «Продолжить показ» игнорируем касания карусели короткое время */
  const dressIgnoreTouchUntilRef = useRef(0);
  /** Логический индекс слайда 0..DRESS_COUNT-1 (как в статьях: счётчик, не из позиции). */
  const dressLogicalIndexRef = useRef(0);

  /** Трек: три полных копии [массив1, массив2, массив3]. Работаем с центральным; при переходе за границу — rewind в центр. */
  const dressTrackSlides = React.useMemo(() => {
    if (DRESS_COUNT <= 0) return [];
    return [...dressCodeImages, ...dressCodeImages, ...dressCodeImages];
  }, [dressCodeImages, DRESS_COUNT]);

  const DRESS_TRACK_LEN = dressTrackSlides.length; // 3 * DRESS_COUNT
  /** Индекс первого слайда центральной копии в DOM (вторая копия). */
  const DRESS_CENTER_START = DRESS_COUNT;
  /** Индекс последнего слайда центральной копии в DOM. */
  const DRESS_CENTER_END = 2 * DRESS_COUNT - 1;

  /** Центр экрана (viewport) в координатах content-box контейнера (где живёт трек). Учитываем padding контейнера. */
  const getDressViewportCenterInContainer = (): number => {
    const track = dressSliderRef.current;
    const container = track?.parentElement;
    if (!container || typeof window === 'undefined') return 0;
    const rect = container.getBoundingClientRect();
    const paddingLeft = parseFloat(getComputedStyle(container).paddingLeft) || 0;
    return window.innerWidth / 2 - rect.left - paddingLeft;
  };

  /** Позиция translateX, при которой слайд domIndex оказывается ровно по центру экрана. */
  const getPositionForDomIndex = (domIndex: number): number => {
    const track = dressSliderRef.current;
    const container = track?.parentElement;
    if (!container || !track || dressCardStep <= 0) return 0;
    const gap = parseFloat(getComputedStyle(track).gap) || 24;
    const cardCenterOffset = gap / 2;
    const slideCenter = (domIndex + 0.5) * dressCardStep - cardCenterOffset;
    const viewportCenterInContainer = getDressViewportCenterInContainer();
    return viewportCenterInContainer - slideCenter;
  };

  dressPausedXRef.current = dressPausedX;

  useEffect(() => {
    if (dressSetWidth > 0 && DRESS_TRACK_LEN > 0) {
      dressInitializedRef.current = true;
      const startX = getPositionForDomIndex(DRESS_CENTER_START);
      dressX.set(startX);
      setDressPausedX(startX);
      dressLogicalIndexRef.current = 0;
    }
  }, [dressSetWidth, dressX, DRESS_COUNT, DRESS_TRACK_LEN]);

  useEffect(() => {
    if (dressPaused || dressSetWidth <= 0 || DRESS_TRACK_LEN <= 0) return;
    const from = getPositionForDomIndex(DRESS_CENTER_START);
    const to = getPositionForDomIndex(DRESS_CENTER_END + 1);
    const fullDuration = 60 * Math.abs(to - from) / (dressCardStep * Math.max(1, DRESS_COUNT));
    const run = () => {
      const start = dressX.get();
      const dist = Math.abs(to - start);
      if (dist < 2) {
        dressX.set(from);
        setDressPausedX(from);
        dressPausedXRef.current = from;
        dressLogicalIndexRef.current = 0;
        run();
        return;
      }
      const duration = fullDuration * (dist / Math.abs(to - from));
      dressControlsRef.current = animate(dressX, [start, to], { duration, ease: 'linear' });
      dressControlsRef.current?.then(() => {
        requestAnimationFrame(() => {
          dressX.set(from);
          setDressPausedX(from);
          dressPausedXRef.current = from;
          dressLogicalIndexRef.current = 0;
          run();
        });
      });
    };
    run();
    return () => {
      dressControlsRef.current?.stop();
      dressControlsRef.current = null;
    };
  }, [dressPaused, dressSetWidth, DRESS_TRACK_LEN]);

  useEffect(() => {
    if (dressPaused) dressX.set(dressPausedX);
  }, [dressPaused, dressPausedX, dressX]);

  /** Снап к ближайшему слайду в центральной копии; если попали в 1-ю или 3-ю копию — переходим в центр (на мобилке rewind без анимации, чтобы не «отматывало»). */
  const snapDressToNearestSlide = (): Promise<void> => {
    const track = dressSliderRef.current;
    const container = track?.parentElement;
    if (!container || !track || dressCardStep <= 0 || DRESS_TRACK_LEN <= 0) return Promise.resolve();
    const viewportCenterInContainer = getDressViewportCenterInContainer();
    const currentX = dressPausedXRef.current;
    const gap = parseFloat(getComputedStyle(track).gap) || 24;
    const cardCenterOffset = gap / 2;
    const viewportCenterInTrack = viewportCenterInContainer - currentX;
    let bestDom = DRESS_CENTER_START;
    let bestDist = Infinity;
    for (let domIndex = 0; domIndex < DRESS_TRACK_LEN; domIndex++) {
      const slideCenter = (domIndex + 0.5) * dressCardStep - cardCenterOffset;
      const d = Math.abs(viewportCenterInTrack - slideCenter);
      if (d < bestDist) {
        bestDist = d;
        bestDom = domIndex;
      }
    }
    let targetX: number;
    const isRewindToCenter = bestDom < DRESS_CENTER_START || bestDom > DRESS_CENTER_END;
    if (bestDom < DRESS_CENTER_START) {
      targetX = getPositionForDomIndex(DRESS_CENTER_END);
      dressLogicalIndexRef.current = DRESS_COUNT - 1;
    } else if (bestDom > DRESS_CENTER_END) {
      targetX = getPositionForDomIndex(DRESS_CENTER_START);
      dressLogicalIndexRef.current = 0;
    } else {
      targetX = getPositionForDomIndex(bestDom);
      dressLogicalIndexRef.current = bestDom - DRESS_CENTER_START;
    }
    dressManualControlsRef.current?.stop();
    dressManualControlsRef.current = null;
    if (isRewindToCenter && !isDesktop) {
      dressX.set(targetX);
      setDressPausedX(targetX);
      dressPausedXRef.current = targetX;
      return Promise.resolve();
    }
    const anim = animate(dressX, targetX, { duration: 0.3, ease: 'easeOut' });
    return anim.then(() => {
      dressX.set(targetX);
      setDressPausedX(targetX);
      dressPausedXRef.current = targetX;
    }) as Promise<void>;
  };

  /** Обновить dressLogicalIndexRef по текущей позиции (логический индекс в центральной копии). */
  const syncDressLogicalIndex = () => {
    const track = dressSliderRef.current;
    const container = track?.parentElement;
    if (!container || !track || dressCardStep <= 0 || DRESS_TRACK_LEN <= 0) return;
    const currentX = dressX.get();
    const viewportCenterInTrack = getDressViewportCenterInContainer() - currentX;
    const gap = parseFloat(getComputedStyle(track).gap) || 24;
    const cardCenterOffset = gap / 2;
    let bestDom = DRESS_CENTER_START;
    let bestDist = Infinity;
    for (let domIndex = 0; domIndex < DRESS_TRACK_LEN; domIndex++) {
      const slideCenter = (domIndex + 0.5) * dressCardStep - cardCenterOffset;
      const d = Math.abs(viewportCenterInTrack - slideCenter);
      if (d < bestDist) {
        bestDist = d;
        bestDom = domIndex;
      }
    }
    if (bestDom < DRESS_CENTER_START) dressLogicalIndexRef.current = DRESS_COUNT - 1;
    else if (bestDom > DRESS_CENTER_END) dressLogicalIndexRef.current = 0;
    else dressLogicalIndexRef.current = bestDom - DRESS_CENTER_START;
  };

  const handleDressPause = () => {
    if (Date.now() < dressIgnoreTouchUntilRef.current) return;
    dressControlsRef.current?.stop();
    dressControlsRef.current = null;
    const currentX = dressX.get();
    setDressPausedX(currentX);
    dressPausedXRef.current = currentX;
    syncDressLogicalIndex();
    setDressPaused(true);
    if (isDesktop) snapDressToNearestSlide();
  };

  const handleDressResume = () => {
    setDressPaused(false);
    dressIgnoreTouchUntilRef.current = Date.now() + 450;
  };

  /** С последнего слайда центра: анимация к первому слайду 3-й копии, затем rewind в первый слайд центра. */
  const handleDressNext = () => {
    dressManualControlsRef.current?.stop();
    dressControlsRef.current?.stop();
    const currentLogical = dressLogicalIndexRef.current;
    const nextLogical = (currentLogical + 1) % DRESS_COUNT;
    const isWrapFromLast = currentLogical === DRESS_COUNT - 1;
    const targetX = isWrapFromLast
      ? getPositionForDomIndex(DRESS_CENTER_END + 1)
      : getPositionForDomIndex(DRESS_CENTER_START + nextLogical);
    const anim = animate(dressX, targetX, { duration: 0.35, ease: 'easeInOut' });
    dressManualControlsRef.current = anim;
    anim.then(() => {
      dressManualControlsRef.current = null;
      if (isWrapFromLast) {
        const rewind = getPositionForDomIndex(DRESS_CENTER_START);
        requestAnimationFrame(() => {
          dressX.set(rewind);
          dressPausedXRef.current = rewind;
          dressLogicalIndexRef.current = 0;
          setDressPausedX(rewind);
        });
      } else {
        setDressPausedX(targetX);
        dressPausedXRef.current = targetX;
        dressLogicalIndexRef.current = nextLogical;
      }
    });
  };

  /** С первого слайда центра: анимация к последнему слайду 1-й копии, затем rewind в последний слайд центра. */
  const handleDressPrev = () => {
    dressManualControlsRef.current?.stop();
    dressControlsRef.current?.stop();
    const currentLogical = dressLogicalIndexRef.current;
    const prevLogical = (currentLogical - 1 + DRESS_COUNT) % DRESS_COUNT;
    const isWrapFromFirst = currentLogical === 0;
    const targetX = isWrapFromFirst
      ? getPositionForDomIndex(DRESS_CENTER_START - 1)
      : getPositionForDomIndex(DRESS_CENTER_START + prevLogical);
    const anim = animate(dressX, targetX, { duration: 0.35, ease: 'easeInOut' });
    dressManualControlsRef.current = anim;
    anim.then(() => {
      dressManualControlsRef.current = null;
      if (isWrapFromFirst) {
        const rewind = getPositionForDomIndex(DRESS_CENTER_END);
        requestAnimationFrame(() => {
          dressX.set(rewind);
          dressPausedXRef.current = rewind;
          dressLogicalIndexRef.current = DRESS_COUNT - 1;
          setDressPausedX(rewind);
        });
      } else {
        setDressPausedX(targetX);
        dressPausedXRef.current = targetX;
        dressLogicalIndexRef.current = prevLogical;
      }
    });
  };

  const handleDressTouchStart = (e: React.TouchEvent) => {
    if (Date.now() < dressIgnoreTouchUntilRef.current) return;
    handleDressPause();
    dressTouchStartRef.current = { x: dressX.get(), clientX: e.touches[0].clientX };
  };

  const handleDressTouchMove = (e: React.TouchEvent) => {
    if (!dressTouchStartRef.current || DRESS_TRACK_LEN <= 0) return;
    const delta = dressTouchStartRef.current.clientX - e.touches[0].clientX;
    let next = dressTouchStartRef.current.x - delta;
    const touchMin = getPositionForDomIndex(DRESS_TRACK_LEN - 1);
    const touchMax = getPositionForDomIndex(0);
    next = Math.max(touchMin, Math.min(touchMax, next));
    dressX.set(next);
    setDressPausedX(next);
  };

  const handleDressTouchEnd = () => {
    if (dressTouchStartRef.current !== null) {
      dressPausedXRef.current = dressX.get();
      setDressPausedX(dressX.get());
      // Снап к реально ближайшему слайду без предварительной нормализации — иначе при долистывании до последнего был скачок на первый
      snapDressToNearestSlide();
    }
    dressTouchStartRef.current = null;
  };

  useEffect(() => {
    if (EVENT_PASSED) return;
    const loadRSVP = async () => {
      try {
        const data = await rsvpAPI.get();
        setRsvp(data.rsvp);
      } catch (error) {
        console.error('Failed to load RSVP:', error);
      }
    };
    loadRSVP();
  }, []);

  const handleRSVP = async (attending: boolean) => {
    setIsLoading(true);
    setMessage('');
    try {
      await rsvpAPI.save(attending);
      setRsvp(attending);
      setMessage(attending ? 'Спасибо за подтверждение!' : 'Нам жаль, что вы не сможете прийти');
    } catch (error: any) {
      setMessage(error.message || 'Ошибка сохранения');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative" style={{ backgroundColor: 'var(--color-cream)' }}>
      <Navigation />

      <div className="event-content max-w-7xl mx-auto px-4 sm:px-6 md:px-6 lg:px-8 py-8 md:py-10 lg:py-12">
        {EVENT_PASSED ? (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-16 md:mb-20 lg:mb-24"
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10 lg:gap-12 xl:gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, y: 20, x: -50 }}
                animate={{ opacity: 1, y: 0, x: 0 }}
                transition={{ delay: 0.2 }}
                className="hidden lg:flex justify-center order-2 lg:order-1 lg:justify-end"
              >
                <div className="relative group max-w-[280px] w-full">
                  <div className="absolute inset-0 rounded-3xl opacity-30 group-hover:opacity-50 transition-opacity"
                       style={{ background: 'var(--gradient-main)' }}></div>
                  <div className="relative p-3 bg-white rounded-3xl shadow-xl">
                    <ImageWithFallback
                      src={heroUrls[PHOTO_PATHS.heroGroom] ?? ''}
                      alt="Иван"
                      className="w-full h-64 md:h-80 object-cover rounded-2xl"
                    />
                    <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 px-6 py-2 bg-white rounded-full shadow-lg">
                      <span className="font-serif text-lg md:text-xl gradient-text font-semibold">Иван</span>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
                className="text-center space-y-6 py-8 px-4 md:px-5 lg:px-6 order-1 lg:order-2"
              >
                <div>
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif gradient-text font-bold mb-2">
                    Иван
                  </h1>
                  <div className="decorative-font text-5xl md:text-6xl lg:text-7xl gradient-text my-4">
                    &
                  </div>
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif gradient-text font-bold">
                    Алина
                  </h1>
                </div>

                <p className="text-xl md:text-2xl font-serif font-semibold gradient-text">
                  Мы поженились!
                </p>

                <p className="text-lg md:text-xl font-serif italic leading-relaxed px-2" style={{ color: 'var(--color-text-light)' }}>
                  Вы подарили этому дню тепло и свет —<br />
                  спасибо, что были рядом
                </p>

                <div className="mt-8 flex flex-col items-center">
                  <div className="elegant-card overflow-visible p-8 md:p-10 w-full max-w-sm flex flex-col items-center text-center">
                    <svg width="0" height="0" className="absolute" aria-hidden="true">
                      <defs>
                        <linearGradient id="eventHeartGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#b8a2c8" />
                          <stop offset="50%" stopColor="#99b8c7" />
                          <stop offset="100%" stopColor="#90c695" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <Heart
                      className="w-8 h-8 md:w-9 md:h-9 mb-5"
                      fill="url(#eventHeartGradient)"
                      stroke="url(#eventHeartGradient)"
                    />
                    <div className="flex w-full flex-col items-center gap-2">
                      <p className="m-0 text-base md:text-lg leading-snug" style={{ color: 'var(--color-text-light)' }}>
                        Нашей семье уже
                      </p>
                      <motion.div
                        key={daysMarried}
                        initial={{ scale: 0.85, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.4, ease: 'easeOut' }}
                        className="flex w-full items-center justify-center overflow-visible py-1"
                      >
                        <span className="gradient-text inline-block text-5xl md:text-6xl font-serif font-bold tabular-nums leading-[1.2] pb-1">
                          {daysMarried}
                        </span>
                      </motion.div>
                      <p className="m-0 text-base md:text-lg font-medium leading-snug" style={{ color: 'var(--color-text-light)' }}>
                        {formatDaysWord(daysMarried)}
                      </p>
                    </div>
                  </div>

                  <p className="mt-6 text-base md:text-lg max-w-md leading-relaxed" style={{ color: 'var(--color-text-light)' }}>
                    Лучшие моменты торжества — смотрите{' '}
                    <Link
                      to="/gallery"
                      className="underline font-medium hover:opacity-90 transition-opacity"
                      style={{ color: 'var(--color-lilac)' }}
                    >
                      фото со свадьбы
                    </Link>
                  </p>
                  <p className="mt-3 text-base md:text-lg max-w-md leading-relaxed" style={{ color: 'var(--color-text-light)' }}>
                    Или смотрите и скачивайте фото на{' '}
                    <a
                      href={PHOTOGRAPHER_GALLERY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline font-medium hover:opacity-90 transition-opacity"
                      style={{ color: 'var(--color-lilac)' }}
                    >
                      сайте фотографа
                    </a>
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20, x: 50 }}
                animate={{ opacity: 1, y: 0, x: 0 }}
                transition={{ delay: 0.3 }}
                className="hidden lg:flex justify-center order-3 lg:order-3 lg:justify-start"
              >
                <div className="relative group max-w-[280px] w-full">
                  <div className="absolute inset-0 rounded-3xl opacity-30 group-hover:opacity-50 transition-opacity"
                       style={{ background: 'var(--gradient-main)' }}></div>
                  <div className="relative p-3 bg-white rounded-3xl shadow-xl">
                    <ImageWithFallback
                      src={heroUrls[PHOTO_PATHS.heroBride] ?? ''}
                      alt="Алина"
                      className="w-full h-64 md:h-80 object-cover rounded-2xl"
                    />
                    <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 px-6 py-2 bg-white rounded-full shadow-lg">
                      <span className="font-serif text-lg md:text-xl gradient-text font-semibold">Алина</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.section>
        ) : (
        /* Hero Section with Photos — до мероприятия */
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16 md:mb-20 lg:mb-24"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10 lg:gap-12 xl:gap-16 items-center">
            {/* Groom Photo */}
            <motion.div
              initial={{ opacity: 0, y: 20, x: -50 }}
              animate={{ opacity: 1, y: 0, x: 0 }}
              transition={{ delay: 0.2 }}
              className="hidden lg:flex justify-center order-2 lg:order-1 lg:justify-end"
            >
              <div className="relative group max-w-[280px] w-full">
                <div className="absolute inset-0 rounded-3xl opacity-30 group-hover:opacity-50 transition-opacity"
                     style={{ background: 'var(--gradient-main)' }}></div>
                <div className="relative p-3 bg-white rounded-3xl shadow-xl">
                  <ImageWithFallback
                    src={heroUrls[PHOTO_PATHS.heroGroom] ?? ''}
                    alt="Иван"
                    className="w-full h-64 md:h-80 object-cover rounded-2xl"
                  />
                  <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 px-6 py-2 bg-white rounded-full shadow-lg">
                    <span className="font-serif text-lg md:text-xl gradient-text font-semibold">Иван</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Names and Info */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className="text-center space-y-6 py-8 px-4 md:px-5 lg:px-6 order-1 lg:order-2"
            >
              <div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif gradient-text font-bold mb-2">
                  Иван
                </h1>
                <div className="decorative-font text-5xl md:text-6xl lg:text-7xl gradient-text my-4">
                  &
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif gradient-text font-bold">
                  Алина
                </h1>
              </div>

              <p className="text-lg md:text-xl" style={{ color: 'var(--color-text-light)' }}>
                Приглашаем вас разделить с нами<br />самый важный день
              </p>

              <div className="flex flex-col sm:flex-row gap-4 md:gap-5 justify-center items-center mt-8">
                <div className="elegant-card p-6 min-w-[160px]">
                  <Calendar className="w-6 h-6 mx-auto mb-2" style={{ color: 'var(--color-lilac)' }} />
                  <div className="text-sm" style={{ color: 'var(--color-text-lighter)' }}>Дата</div>
                  <div className="text-2xl font-serif font-semibold gradient-text">22 мая</div>
                </div>

                <div className="elegant-card p-6 min-w-[160px]">
                  <Clock className="w-6 h-6 mx-auto mb-2" style={{ color: 'var(--color-green)' }} />
                  <div className="text-sm" style={{ color: 'var(--color-text-lighter)' }}>Время</div>
                  <div className="text-2xl font-serif font-semibold gradient-text">15:30</div>
                </div>
              </div>
            </motion.div>

            {/* Bride Photo */}
            <motion.div
              initial={{ opacity: 0, y: 20, x: 50 }}
              animate={{ opacity: 1, y: 0, x: 0 }}
              transition={{ delay: 0.3 }}
              className="hidden lg:flex justify-center order-3 lg:order-3 lg:justify-start"
            >
              <div className="relative group max-w-[280px] w-full">
                <div className="absolute inset-0 rounded-3xl opacity-30 group-hover:opacity-50 transition-opacity"
                     style={{ background: 'var(--gradient-main)' }}></div>
                <div className="relative p-3 bg-white rounded-3xl shadow-xl">
                  <ImageWithFallback
                    src={heroUrls[PHOTO_PATHS.heroBride] ?? ''}
                    alt="Алина"
                    className="w-full h-64 md:h-80 object-cover rounded-2xl"
                  />
                  <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 px-6 py-2 bg-white rounded-full shadow-lg">
                    <span className="font-serif text-lg md:text-xl gradient-text font-semibold">Алина</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.section>
        )}

        {!EVENT_PASSED && (
        /* RSVP Section — до мероприятия */
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-16 md:mb-20"
        >
          <div className="elegant-card p-6 md:p-8 lg:p-10 max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-serif gradient-text mb-4">
              Подтвердите присутствие
            </h2>
            <p className="mb-6 md:mb-8 text-base md:text-lg" style={{ color: 'var(--color-text-light)' }}>
              Пожалуйста, сообщите нам, сможете ли вы присутствовать на празднике
            </p>

            <div className="flex flex-col sm:flex-row gap-4 md:gap-5 justify-center mb-6">
              <button
                onClick={() => handleRSVP(true)}
                disabled={isLoading}
                className={`px-8 py-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                  rsvp === true ? 'text-white shadow-lg' : 'bg-white hover:shadow-md'
                }`}
                style={
                  rsvp === true
                    ? { background: 'var(--gradient-main)' }
                    : { color: 'var(--color-text)', borderWidth: '2px', borderColor: 'var(--color-border)' }
                }
              >
                <Check className="w-5 h-5" />
                Буду присутствовать
              </button>

              <button
                onClick={() => handleRSVP(false)}
                disabled={isLoading}
                className={`px-8 py-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                  rsvp === false ? 'bg-gray-400 text-white shadow-lg' : 'bg-white hover:shadow-md'
                }`}
                style={
                  rsvp === false
                    ? {}
                    : { color: 'var(--color-text)', borderWidth: '2px', borderColor: 'var(--color-border)' }
                }
              >
                <X className="w-5 h-5" />
                Не смогу прийти
              </button>
            </div>

            <AnimatePresence mode="wait">
              {message && (
                <motion.div
                  key={message}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                  className="p-4 rounded-lg"
                  style={{
                    background: 'linear-gradient(135deg, rgba(184, 162, 200, 0.1), rgba(144, 198, 149, 0.1))',
                    color: 'var(--color-text)'
                  }}
                >
                  {message}
                </motion.div>
              )}
            </AnimatePresence>

            {PREFERENCES_ENABLED && (
              <p className="mt-5 text-base md:text-lg" style={{ color: 'var(--color-text-light)' }}>
                Можете также указать предпочтения по меню —{' '}
                <Link to="/preferences" className="underline font-medium hover:opacity-90 transition-opacity" style={{ color: 'var(--color-lilac)' }}>
                  заполнить предпочтения
                </Link>
              </p>
            )}

            <div className="mt-8 pt-6 border-t border-[var(--color-border)]/50">
              <p className="text-base md:text-lg" style={{ color: 'var(--color-text-light)' }}>
                По вопросам творческого поздравления вы можете обратиться к организаторам:
              </p>
              <div className="mt-2 flex flex-col gap-1">
                <p className="text-base md:text-lg">
                  <span className="font-medium" style={{ color: 'var(--color-text)' }}>Кристина:</span>{' '}
                  <a
                    href="tel:+79879115940"
                    className="underline font-medium hover:opacity-90 transition-opacity"
                    style={{ color: 'var(--color-lilac)' }}
                  >
                    +7 (987) 911-59-40
                  </a>
                </p>
                <p className="text-base md:text-lg">
                  <span className="font-medium" style={{ color: 'var(--color-text)' }}>Мария:</span>{' '}
                  <a
                    href="tel:+79397513536"
                    className="underline font-medium hover:opacity-90 transition-opacity"
                    style={{ color: 'var(--color-lilac)' }}
                  >
                    +7 (939) 751-35-36
                  </a>
                </p>
              </div>
            </div>
          </div>

          {user?.friend === false && (
            <div className="elegant-card p-6 md:p-8 lg:p-10 max-w-2xl mx-auto text-center mt-6">
              <p className="text-base md:text-lg" style={{ color: 'var(--color-text-light)' }}>
                Если думаете о подарке — мы будем искренне рады подарку в конверте, так нам проще воплотить в жизнь наши мечты.
              </p>
            </div>
          )}
        </motion.section>
        )}

        {/* About Event - Timeline */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mb-16 md:mb-20"
        >
          <div className="text-center mb-8 md:mb-10 lg:mb-12">
            <h2 className="text-3xl md:text-4xl font-serif gradient-text mb-4">
              О событии
            </h2>
            <p className="text-lg md:text-xl" style={{ color: 'var(--color-text-light)' }}>
              Программа торжества
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="relative pl-0 md:pl-0">
              {/* Timeline Line - слева на мобилке, по центру на десктопе */}
              <div
                className="absolute left-6 md:left-1/2 top-0 bottom-0 w-0.5 md:-translate-x-1/2"
                style={{ background: 'var(--gradient-main)', opacity: 0.3 }}
              />

              {timeline.map((item, index) => (
                <motion.div
                  key={index}
                  initial={
                    isDesktop
                      ? { opacity: 0, x: index % 2 === 0 ? -30 : 30 }
                      : { opacity: 0, y: -20 }
                  }
                  whileInView={
                    isDesktop ? { opacity: 1, x: 0 } : { opacity: 1, y: 0 }
                  }
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative flex flex-row items-center gap-4 mb-8 md:mb-12 pt-0 min-h-0 md:block"
                >
                  {/* Кружок с временем на линии: на мобилке flex-элемент для выравнивания по центру с карточкой, на десктопе absolute по центру */}
                  <div className="flex-shrink-0 z-10 md:absolute md:left-1/2 md:top-0 md:-translate-x-1/2">
                    <div
                      className="w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center text-white font-bold shadow-lg text-sm md:text-base"
                      style={{ background: 'var(--gradient-main)' }}
                    >
                      {item.time}
                    </div>
                  </div>

                  {/* Карточка: справа от кружка на мобилке (gap), чередуется на десктопе */}
                  <div
                    className={`flex-1 min-w-0 md:flex-initial md:min-w-0 md:ml-0 flex flex-col md:flex-row md:items-center md:gap-4 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div
                      className={`flex-1 ${index % 2 === 0 ? 'md:text-right md:mr-3' : 'md:text-left md:ml-3'} text-left md:text-inherit`}
                    >
                      <div className="elegant-card p-5 md:p-6 md:inline-block md:block">
                        <div className="flex items-center gap-3 justify-start md:justify-start">
                          <item.icon
                            className="w-6 h-6 flex-shrink-0"
                            style={{ color: index % 2 === 0 ? 'var(--color-lilac)' : 'var(--color-green)' }}
                          />
                          <h3 className="text-xl md:text-2xl font-serif font-semibold" style={{ color: 'var(--color-text)' }}>
                            {item.title}
                          </h3>
                        </div>
                        <p className="mt-2 text-sm" style={{ color: 'var(--color-text-light)' }}>
                          {item.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex-1 hidden md:block" />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Color Palette Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="mb-16 md:mb-20"
        >
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Palette className="w-8 h-8" style={{ color: 'var(--color-lilac)' }} />
              <h2 className="text-3xl md:text-4xl font-serif gradient-text">
                Палитра
              </h2>
            </div>
            <p className="text-lg mb-6" style={{ color: 'var(--color-text-light)' }}>
              Цветовая гамма нашей свадьбы
            </p>

            {/* Category Filters */}
            <div className="flex flex-wrap justify-center gap-2 mb-8 max-w-4xl mx-auto">
              {paletteCategories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === category ? 'text-white shadow-lg' : 'bg-white hover:shadow-md'
                  }`}
                  style={
                    selectedCategory === category
                      ? { background: 'var(--gradient-main)' }
                      : { color: 'var(--color-text)' }
                  }
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Compact Color Grid */}
          <div className="max-w-6xl mx-auto elegant-card p-6 md:p-8">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={selectedCategory}
                className="flex flex-wrap justify-center gap-3"
                initial={{ opacity: 0, filter: 'blur(10px)' }}
                animate={{ opacity: 1, filter: 'blur(0px)' }}
                exit={{ opacity: 0, filter: 'blur(10px)' }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                {filteredColors.map((color, index) => (
                  <motion.div
                    key={color.hex}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.02, duration: 0.2, ease: 'easeOut' }}
                    whileHover={{ scale: 1.05 }}
                    className="relative group cursor-pointer w-10 h-10 md:w-24 md:h-24"
                    onClick={() => setSelectedColor(color)}
                  >
                    <div
                      className="w-full h-full rounded-full shadow-md group-hover:shadow-xl transition-all ring-2 ring-white group-hover:ring-4"
                      style={{ backgroundColor: color.hex }}
                    />
                    <div className="hidden md:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                      <div className="bg-white px-3 py-2 rounded-lg shadow-xl whitespace-nowrap text-xs font-medium"
                           style={{ color: 'var(--color-text)' }}>
                        {color.name}
                        <div className="text-[10px] opacity-60 font-mono">{color.hex}</div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.section>

        {/* Dress Code with Slider */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="mb-16 md:mb-20"
        >
          <div className="text-center mb-6 md:mb-8">
            <h2 className="text-3xl md:text-4xl font-serif gradient-text mb-4">
              Дресс-код
            </h2>
            <div className="elegant-card p-5 md:p-6 lg:p-8 max-w-2xl mx-auto">
              <p className="text-xl md:text-2xl font-serif font-semibold mb-3 md:mb-4" style={{ color: 'var(--color-text)' }}>
                Пастельные тона
              </p>
              <p className="text-sm md:text-base" style={{ color: 'var(--color-text-light)' }}>
                Элегантный вечерний наряд в пастельных и светлых тонах. Приветствуются нежные оттенки, 
                без ярких принтов, перьев, страз и объёмных элементов — пусть образ будет изящным и в духе праздника.
              </p>
            </div>
          </div>

          {/* Infinite Slider — замыкается: после последнего снова первое; пауза по наведению/касанию, листание стрелками или свайпом */}
          <div
            className="relative overflow-hidden py-8 px-[10vw] md:px-[15vw]"
            onMouseEnter={handleDressPause}
            onMouseLeave={handleDressResume}
            onTouchStart={handleDressTouchStart}
            onTouchMove={handleDressTouchMove}
            onTouchEnd={handleDressTouchEnd}
            onTouchCancel={handleDressTouchEnd}
          >
            <motion.div
              ref={dressSliderRef}
              className="flex gap-6"
              style={{ x: dressX, minWidth: 'max-content' }}
            >
              {dressTrackSlides.map((image, index) => (
                <div key={`dress-${index}`} className="flex-shrink-0 w-64 md:w-80">
                  <div className="elegant-card p-3 overflow-hidden">
                    {image ? (
                      <ImageWithFallback
                        src={image}
                        alt={`Dress code example ${index + 1}`}
                        className="w-full h-80 md:h-96 object-cover rounded-xl"
                      />
                    ) : (
                      <div className="w-full h-80 md:h-96 rounded-xl inline-block bg-gray-100 text-center align-middle">
                        <div className="flex items-center justify-center w-full h-full" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </motion.div>

            {/* Кнопки листания при паузе */}
            {dressPaused && (
              <>
                <button
                  type="button"
                  onClick={handleDressPrev}
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-opacity hover:opacity-90"
                  style={{ background: 'var(--gradient-main)', color: 'white' }}
                  aria-label="Назад"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  type="button"
                  onClick={handleDressNext}
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-opacity hover:opacity-90"
                  style={{ background: 'var(--gradient-main)', color: 'white' }}
                  aria-label="Вперёд"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
                {/* Обёртка с увеличенной зоной нажатия: в capture останавливаем touch, чтобы карусель не получала касания; «Продолжить» вызываем в onTouchEndCapture и по клику */}
                <div
                  className="absolute bottom-0 left-1/2 -translate-x-1/2 z-20 p-4 md:hidden"
                  onTouchStartCapture={(e) => e.stopPropagation()}
                  onTouchEndCapture={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleDressResume();
                  }}
                  onTouchCancelCapture={(e) => e.stopPropagation()}
                  onTouchMoveCapture={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDressResume();
                    }}
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                    className="text-sm px-4 py-2 min-h-[40px] rounded-full bg-white/95 shadow-md text-[var(--color-text)] font-medium"
                  >
                    Продолжить показ
                  </button>
                </div>
              </>
            )}

            {/* Gradient Overlays */}
            <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-[var(--color-cream)] to-transparent pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-[var(--color-cream)] to-transparent pointer-events-none" />
          </div>
        </motion.section>

        {/* Venue Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="mb-16 md:mb-20"
        >
          <div className="text-center mb-6 md:mb-8">
            <h2 className="text-3xl md:text-4xl font-serif gradient-text mb-4">
              Место проведения
            </h2>
          </div>

          <div className="elegant-card p-5 md:p-6 lg:p-8 max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-6">
              <MapPin className="w-6 h-6" style={{ color: 'var(--color-lilac)' }} />
              <h3 className="text-xl md:text-2xl font-serif font-semibold" style={{ color: 'var(--color-text)' }}>
                Sky-village, Самарская область
              </h3>
            </div>

            {/* Виджет карты, под ней блок «Показать на карте» и ссылки (мобилка и десктоп). */}
            <div className="rounded-2xl overflow-hidden shadow-lg h-[280px] md:h-[400px] lg:h-[480px] mb-3">
              <iframe
                src={`https://yandex.ru/map-widget/v1/?ll=${LOCATION_COORDS.lng},${LOCATION_COORDS.lat}&z=15&l=map&pt=${LOCATION_COORDS.lng},${LOCATION_COORDS.lat},pm2rdm`}
                width="100%"
                height="100%"
                frameBorder="0"
                allowFullScreen
                style={{ position: 'relative' }}
              />
            </div>
            <div className="rounded-2xl overflow-hidden">
              <a
                href={useMobileMap ? geoUri : yandexMapsUrl}
                target={useMobileMap ? undefined : '_blank'}
                rel={useMobileMap ? undefined : 'noopener noreferrer'}
                onClick={useMobileMap ? handleMapClick : undefined}
                className="flex items-center justify-center gap-2 py-3 px-6 rounded-xl border-2 font-medium transition-all hover:shadow-md active:scale-[0.99] cursor-pointer"
                style={{ borderColor: 'var(--color-lilac)', color: 'var(--color-text)', backgroundColor: 'var(--color-white)', outline: 'none' }}
              >
                <MapPin className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--color-lilac)' }} />
                <span>Показать на карте</span>
              </a>
              <div className="flex flex-wrap justify-center gap-x-2 gap-y-1 px-3 pt-2 pb-3 text-xs" style={{ backgroundColor: 'transparent', color: 'var(--color-text-light)' }}>
                <span>Открыть в:</span>
                <a href={yandexMapsUrl} target="_blank" rel="noopener noreferrer" className="underline hover:opacity-80 cursor-pointer" style={{ color: 'var(--color-text)' }}>Яндекс</a>
                <span>·</span>
                <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer" className="underline hover:opacity-80 cursor-pointer" style={{ color: 'var(--color-text)' }}>Google</a>
              </div>
            </div>
          </div>
        </motion.section>
      </div>

      {/* Color Details Modal */}
      <AnimatePresence>
        {selectedColor && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
            onClick={() => setSelectedColor(null)}
          >
            <div
              className="elegant-card p-8 md:p-10 max-w-md w-full"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <div
                className="w-32 h-32 mx-auto rounded-full shadow-2xl mb-6 ring-4 ring-white"
                style={{ backgroundColor: selectedColor.hex }}
              />

              <h2 className="text-3xl md:text-4xl font-serif font-bold text-center mb-6 gradient-text">
                {selectedColor.name}
              </h2>

              <div className="flex flex-col gap-3 mb-6">
                <div className="flex items-center justify-between p-3 rounded-lg"
                     style={{ backgroundColor: 'var(--color-cream-light)' }}>
                  <div>
                    <div className="text-xs opacity-60 mb-1" style={{ color: 'var(--color-text-lighter)' }}>HEX</div>
                    <div className="font-mono font-semibold" style={{ color: 'var(--color-text)' }}>
                      {selectedColor.hex.toUpperCase()}
                    </div>
                  </div>
                  <button
                    onClick={() => { navigator.clipboard.writeText(selectedColor.hex); toast.success('HEX скопирован!'); }}
                    className="p-2 rounded-lg hover:bg-white/50 transition-colors"
                  >
                    <Copy className="w-5 h-5" style={{ color: 'var(--color-lilac)' }} />
                  </button>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg"
                     style={{ backgroundColor: 'var(--color-cream-light)' }}>
                  <div>
                    <div className="text-xs opacity-60 mb-1" style={{ color: 'var(--color-text-lighter)' }}>RGB</div>
                    <div className="font-mono font-semibold text-sm" style={{ color: 'var(--color-text)' }}>
                      {selectedColor.rgb}
                    </div>
                  </div>
                  <button
                    onClick={() => { navigator.clipboard.writeText(selectedColor.rgb); toast.success('RGB скопирован!'); }}
                    className="p-2 rounded-lg hover:bg-white/50 transition-colors"
                  >
                    <Copy className="w-5 h-5" style={{ color: 'var(--color-green)' }} />
                  </button>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="font-serif font-semibold mb-2" style={{ color: 'var(--color-text)' }}>Описание</h3>
                <p className="text-sm" style={{ color: 'var(--color-text-light)' }}>{selectedColor.description}</p>
              </div>

              <button
                onClick={() => setSelectedColor(null)}
                className="w-full py-3 rounded-xl text-white font-semibold transition-all hover:shadow-lg"
                style={{ background: 'var(--gradient-main)' }}
              >
                Закрыть
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
